/* eslint-disable @typescript-eslint/no-explicit-any */

'use client';

import { useDebounce } from '@/hooks/useDebounce';
import { faClock } from '@fortawesome/free-regular-svg-icons/faClock';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons/faCircleXmark';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropsWithClasses } from '@lamesarv-sdk/types';
import React, { SyntheticEvent, useCallback, useRef, useState } from 'react';
import Autosuggest, { RenderSuggestion } from 'react-autosuggest';
import { twMerge } from 'tailwind-merge';

// Autosuggest is incompatible with TypeScript types, so we cast it to any
const AutosuggestWrapper = Autosuggest as unknown as React.FC<any>;

export interface ISuggestion {
  _type: string;
  _code: string;
  _title: string;
  _description: string;
  _url?: string;
}

export interface ISuggestions {
  sectionTitle: string;
  suggestions: ISuggestion[];
}

const Suggestion = ({ suggestion }: { suggestion: ISuggestion }) => {
  return (
    <div className="flex flex-col gap-2" data-code={suggestion._code}>
      <div className="text-sm font-semibold">{suggestion._title}</div>
      <div className="text-xs">{suggestion._description}</div>
    </div>
  );
};

// --

export interface IAutoCompleteProps extends PropsWithClasses {
  placeholder?: string;
  onChange?: (value: string) => void;
  onEnter?: (value: string, suggestions: ISuggestions[]) => void;
  onKeyDown?: (value: string, suggestions: ISuggestions[]) => void;
  onSuggestionsFetch: (value: string) => Promise<ISuggestions[]>;
  onSuggestionsClear?: () => Promise<void>;
  onSuggestionSelected: (suggestion: ISuggestion) => Promise<void>;
  isLoading?: boolean;
  isError?: boolean;
  debounceMilliseconds?: number;
  classSuggestionsSectionTitle?: string;
  customSuggestionMap?: { [type: string]: React.FC<{ suggestion: ISuggestion; query: string }> };
  defaultSearch?: string;
}

export const AutoCompleteBase = (props: IAutoCompleteProps) => {
  const [value, setValue] = useState<string>(props.defaultSearch ?? '');
  const suggestionsDebounceRef = useRef<{
    callback: ((suggestions: ISuggestions[]) => void) | null;
    isInDebounce: boolean;
  }>({
    callback: null,
    isInDebounce: false,
  });
  const [suggestions, setSuggestions] = useState<ISuggestions[]>([]);
  const enterWasPressed = useRef<boolean>(false);

  // -- Events

  const [onSuggestionsFetchRequested, { clearTimer: rawClearTimer }] = useDebounce(
    async (request: Autosuggest.SuggestionsFetchRequestedParams) => {
      if (request.reason === 'input-changed') {
        suggestionsDebounceRef.current.isInDebounce = false;

        const _suggestions = await props.onSuggestionsFetch(request.value);

        if (suggestionsDebounceRef.current.callback) {
          suggestionsDebounceRef.current.callback(_suggestions);
          suggestionsDebounceRef.current.callback = null;
        }

        setSuggestions(_suggestions);
      }
    },
    props.debounceMilliseconds ?? 1000,
  );

  const clearTimer = () => {
    rawClearTimer();
    suggestionsDebounceRef.current = {
      callback: null,
      isInDebounce: false,
    };
    enterWasPressed.current = false;
  };

  const onSuggestionsClearRequested = () => {
    clearTimer();

    if (value) return;

    setSuggestions([]);
    if (props.onSuggestionsClear) {
      props.onSuggestionsClear();
    }
  };

  const onSuggestionSelected = (_e: SyntheticEvent, { suggestion }: { suggestion: ISuggestion }) => {
    props.onSuggestionSelected(suggestion);
  };

  // -- Render

  const getSectionSuggestions = (item: ISuggestions): any => {
    return item.suggestions;
  };

  const renderSectionTitle = (item: ISuggestions) => {
    return <div className={props.classSuggestionsSectionTitle}>{item.sectionTitle}</div>;
  };

  const getSuggestionValue = (suggestion: ISuggestion) => {
    return suggestion._code;
  };

  const renderSuggestion: RenderSuggestion<ISuggestion> = (suggestion, { query }) => {
    if (props.customSuggestionMap && props.customSuggestionMap[suggestion._type]) {
      const CustomSuggestion = props.customSuggestionMap[suggestion._type];
      return <CustomSuggestion suggestion={suggestion} query={query} />;
    }

    return <Suggestion suggestion={suggestion} />;
  };

  // -- Input

  const inputOnChange = (_e: SyntheticEvent, { newValue }: { newValue: string }) => {
    setValue(newValue);
    setSuggestions([]);

    if (props.onChange) {
      props.onChange(newValue);
    }
  };

  const inputOnKeyDown = useCallback(
    async (e: unknown) => {
      if (!value) return;

      if ((e as KeyboardEvent).key === 'Enter') {
        if (props.onEnter) {
          enterWasPressed.current = true;
          if (suggestionsDebounceRef.current.isInDebounce) {
            suggestionsDebounceRef.current.callback = (suggestions) => {
              if (props.onEnter) props.onEnter(value, suggestions);
            };
          } else {
            props.onEnter(value, suggestions);
          }
        }
      } else {
        clearTimer();

        if (props.onKeyDown) {
          props.onKeyDown(value, suggestions);
        }
      }
    },
    [value, suggestions],
  );

  const inputProps = {
    placeholder: props.placeholder ?? 'Search Items',
    value,
    onChange: inputOnChange,
    onKeyDown: inputOnKeyDown,
  };

  // -- Component

  return (
    <div className={twMerge('w-full relative', props.className)}>
      <AutosuggestWrapper
        multiSection={true}
        suggestions={suggestions}
        onSuggestionsFetchRequested={(req: Autosuggest.SuggestionsFetchRequestedParams) => {
          suggestionsDebounceRef.current.isInDebounce = true;
          return onSuggestionsFetchRequested(req);
        }}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        containerProps={{ className: 'test-class w-full' }}
        getSectionSuggestions={getSectionSuggestions}
        inputProps={inputProps}
        shouldRenderSuggestions={(value: string) => {
          return value.trim().length > 0 && !enterWasPressed.current;
        }}
      />
      <div className={twMerge('absolute top-1/2 left-2 -translate-y-1/2', props.classIcon)}>
        {!props.isError && !props.isLoading && (
          <FontAwesomeIcon icon={faMagnifyingGlass} className="w-6 fill-gray-400" />
        )}
        {!props.isError && props.isLoading && <FontAwesomeIcon icon={faClock} className="w-6 fill-gray-400" />}
        {props.isError && <FontAwesomeIcon icon={faCircleXmark} className="w-6 fill-gray-400" />}
      </div>
    </div>
  );
};
